import React, { useRef, useEffect, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import { Stroke, Point } from './types';

interface DrawableFloorPlanProps {
  imageUrl: string;
  alt: string;
  unitId: string | number;
  isDrawMode: boolean;
  activeColor: string;
  lineWidth: number;
  activeTool: 'pen' | 'eraser';
  strokes: Stroke[];
  onStrokesChange: (unitId: string | number, strokes: Stroke[]) => void;
  /** Optional rotation in degrees (0, 90, 180, 270). Used for pointer-to-canvas coord conversion when parent applies the visual rotation. */
  rotationDegrees?: number;
  /** When true, rotation is applied by parent container - do not apply transform here, but still use rotationDegrees for coord conversion. */
  rotationAppliedByParent?: boolean;
  /** When true, the plan is mirrored horizontally (scaleX(-1)). Applied in screen space (outermost transform) and inverted in pointer-to-canvas coord conversion. */
  flipHorizontal?: boolean;
  /** When true, the horizontal flip is applied by the parent container - do not apply transform here, but still use flipHorizontal for coord conversion. */
  flipAppliedByParent?: boolean;
}

const renderStrokes = (ctx: CanvasRenderingContext2D, strokes: Stroke[], dpr: number) => {
  ctx.save();
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  for (const stroke of strokes) {
    if (stroke.points.length < 2) continue;
    ctx.beginPath();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = stroke.width * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
    }

    ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
    for (let i = 1; i < stroke.points.length; i++) {
      ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
    }
    ctx.stroke();
  }
  ctx.restore();
};

const DrawableFloorPlan: React.FC<DrawableFloorPlanProps> = ({
  imageUrl,
  alt,
  unitId,
  isDrawMode,
  activeColor,
  lineWidth,
  activeTool,
  strokes,
  onStrokesChange,
  rotationDegrees = 0,
  rotationAppliedByParent = false,
  flipHorizontal = false,
  flipAppliedByParent = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const isDrawingRef = useRef(false);
  const currentStrokeRef = useRef<Point[]>([]);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Size canvas to match image
  const sizeCanvas = useCallback(() => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas || !imageLoaded) return;

    const dpr = window.devicePixelRatio || 1;
    const w = img.clientWidth;
    const h = img.clientHeight;

    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.width = w * dpr;
    canvas.height = h * dpr;

    const ctx = canvas.getContext('2d');
    if (ctx) renderStrokes(ctx, strokes, dpr);
  }, [imageLoaded, strokes]);

  useEffect(() => {
    sizeCanvas();
  }, [sizeCanvas]);

  // Re-render strokes when they change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    renderStrokes(ctx, strokes, dpr);
  }, [strokes]);

  // Resize observer
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const ro = new ResizeObserver(() => sizeCanvas());
    ro.observe(img);
    return () => ro.disconnect();
  }, [sizeCanvas]);

  const getCanvasPoint = useCallback((e: React.PointerEvent): Point | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    let localX = e.clientX - centerX;
    let localY = e.clientY - centerY;

    // The visual transform is `scaleX(-1) rotate(deg)` (flip outermost, in
    // screen space). Inverting screen -> canvas therefore undoes the flip
    // first (negate X), then undoes the rotation below.
    if (flipHorizontal) {
      localX = -localX;
    }

    // Apply inverse rotation so pointer coords match the rotated canvas
    if (rotationDegrees) {
      const r = ((rotationDegrees % 360) + 360) % 360;
      if (r !== 0) {
        const theta = (-r * Math.PI) / 180;
        const cos = Math.cos(theta);
        const sin = Math.sin(theta);
        const rotatedX = localX * cos - localY * sin;
        const rotatedY = localX * sin + localY * cos;
        localX = rotatedX;
        localY = rotatedY;
      }
    }

    // Map from canvas local space (centered) to canvas pixel coords
    // When rotated 90/270, canvas axes swap relative to bounding rect
    const r = rotationDegrees ? ((rotationDegrees % 360) + 360) % 360 : 0;
    const swapAxes = r === 90 || r === 270;
    const W = swapAxes ? rect.height : rect.width;
    const H = swapAxes ? rect.width : rect.height;
    const scaleX = canvas.width / (W * dpr);
    const scaleY = canvas.height / (H * dpr);
    return {
      x: (localX + W / 2) * scaleX,
      y: (localY + H / 2) * scaleY,
    };
  }, [rotationDegrees, flipHorizontal]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!isDrawMode) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = getCanvasPoint(e);
    if (!pt) return;
    isDrawingRef.current = true;
    currentStrokeRef.current = [pt];
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [isDrawMode, getCanvasPoint]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDrawingRef.current || !isDrawMode) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = getCanvasPoint(e);
    if (!pt) return;
    currentStrokeRef.current.push(pt);

    // Draw incrementally for smooth feedback
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const points = currentStrokeRef.current;

    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.beginPath();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (activeTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = lineWidth * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = activeColor;
      ctx.lineWidth = lineWidth;
    }

    if (points.length >= 2) {
      ctx.moveTo(points[points.length - 2].x, points[points.length - 2].y);
      ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
      ctx.stroke();
    }
    ctx.restore();
  }, [isDrawMode, getCanvasPoint, activeColor, lineWidth, activeTool]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    isDrawingRef.current = false;

    if (currentStrokeRef.current.length >= 2) {
      const newStroke: Stroke = {
        points: currentStrokeRef.current,
        color: activeColor,
        width: lineWidth,
        tool: activeTool,
      };
      onStrokesChange(unitId, [...strokes, newStroke]);
    }
    currentStrokeRef.current = [];
  }, [activeColor, lineWidth, activeTool, strokes, onStrokesChange, unitId]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...((() => {
          // Flip is applied outermost (screen space), rotation innermost:
          // `scaleX(-1) rotate(deg)`. Skip whichever the parent already applies.
          const flip = !flipAppliedByParent && flipHorizontal ? 'scaleX(-1)' : '';
          const rotate = !rotationAppliedByParent && rotationDegrees ? `rotate(${rotationDegrees}deg)` : '';
          const transform = [flip, rotate].filter(Boolean).join(' ');
          return transform ? { transform, transformOrigin: 'center center' } : {};
        })()),
      }}
    >
      <Box
        component="img"
        ref={imgRef}
        src={imageUrl}
        alt={alt}
        onLoad={() => setImageLoaded(true)}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: '100%',
          maxHeight: '100%',
          userSelect: 'none',
        }}
        draggable={false}
      />
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: isDrawMode ? 'auto' : 'none',
          cursor: isDrawMode ? 'crosshair' : 'default',
          touchAction: isDrawMode ? 'none' : 'auto',
        }}
      />
    </Box>
  );
};

export default DrawableFloorPlan;
